import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Modal from '../common/Modal';
import AddressModal from '../../components/common/AddressModal';
import { FlyInTransition } from '../../components/transitions/transitions';
import StyledTappable from '../../components/common/StyledTappable';
import { FaTimes } from 'react-icons/fa';
import { SERVICE_LINE_NAMES } from '../../config';

import profileIcon from '../../images/lab-test/profile.svg';
import Morning from '../../images/home/NewMorning.svg';
import Afternoon from '../../images/home/NewAfternoon.svg';
import Evening from '../../images/home/NewEvening.svg';
import NewTickMark from '../../images/home/newTickMark.svg';
import {
  getPatients,
  selectPatient,
  selectAddress,
  getLabSlots,
  selectSlot,
  deleteCart,
} from '../../services';
import { logEvent } from '../../utils/logEvent';

const CloseContainer = styled(StyledTappable)`
  -webkit-tap-highlight-color: transparent;
`;

const CloseIcon = (props) => <FaTimes {...props} />;

function safeArray(val) {
  return Array.isArray(val) ? val : [];
}

function getDaysDiffFromToday(dateStr) {
  const today = new Date();
  const target = new Date(dateStr);
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
}

/** Full-screen dim behind bottom sheet — suppress tap flash on overlay + all nested nodes. */
const UpsellModalOverlay = styled.div`
  -webkit-tap-highlight-color: transparent;
  &,
  & * {
    -webkit-tap-highlight-color: transparent;
  }
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
`;

/** Bottom sheet panel — same for patient / address / slot modals; covers every descendant tap target. */
const UpsellModalSheetPanel = styled.div`
  -webkit-tap-highlight-color: transparent;
  &,
  & * {
    -webkit-tap-highlight-color: transparent;
  }
  background: #ffffff;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  max-height: 85vh;
  overflow: hidden;
  touch-action: manipulation;
`;

const UpsellSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding-bottom: 20px;
`;

const UpsellSlotGridEmpty = styled.div`
  grid-column: 1 / -1;
`;

const UpsellSlotButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 12px 8px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  border-radius: 12px;
  position: relative;
  background: #ffffff;
  border: 1px solid ${(p) => (p.$selected ? '#714FFF' : '#EBEAF5')};
  opacity: ${(p) => (p.$enabled ? 1 : 0.4)};
  pointer-events: ${(p) => (p.$enabled ? 'auto' : 'none')};
`;

const UpsellBookingFlowModals = forwardRef(function UpsellBookingFlowModals(
  { deleteCartOnAbandon = false },
  ref
) {
  const history = useHistory();
  const authToken = useSelector((s) => s.user.authToken);

  const [cartId, setCartId] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [onComplete, setOnComplete] = useState(null);

  const [loading, setLoading] = useState(false);

  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showSlotsModal, setShowSlotsModal] = useState(false);

  const [patients, setPatients] = useState([]);
  const [slotsDays, setSlotsDays] = useState([]);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const startedRef = useRef(false);
  const visitTypeRef = useRef(null);
  const bookingForRef = useRef(null);

  const reset = useCallback(() => {
    startedRef.current = false;
    visitTypeRef.current = null;
    bookingForRef.current = null;
    setLoading(false);
    setShowPatientModal(false);
    setShowAddressModal(false);
    setShowSlotsModal(false);
    setPatients([]);
    setSlotsDays([]);
    setSelectedPatient(null);
    setSelectedAddress(null);
    setSelectedDate(null);
    setSelectedSlotId(null);
    setSelectedSlot(null);
    setActiveTabIndex(0);
    setCartId(null);
    setPackageId(null);
    setOnComplete(null);
  }, []);

  const openPatients = useCallback(
    (nextCartId, nextPackageId) => {
      setLoading(true);
      return getPatients(authToken, null, nextCartId)
        .then((res) => {
          setPatients(safeArray(res.patients));
          setShowPatientModal(true);
          return res;
        })
        .finally(() => setLoading(false));
    },
    [authToken]
  );

  const openSlots = useCallback(
    (nextCartId) => {
      setLoading(true);
      return getLabSlots(nextCartId, authToken)
        .then((res) => {
          const days = safeArray(res.slots);
          setSlotsDays(days);
          if (!selectedDate && days?.[0]?.date) setSelectedDate(days[0].date);
          setShowSlotsModal(true);
          logEvent('Slot Selection Screen Opened', {
            cartId: nextCartId,
            visitType: visitTypeRef.current,
            bookingFor: bookingForRef.current,
            type: 'upsell',
          });
          return res;
        })
        .finally(() => setLoading(false));
    },
    [authToken, selectedDate]
  );

  const start = useCallback(
    async ({
      cartId: cId,
      packageId: pId,
      onComplete: cb,
      visitType: vt,
      bookingFor: bf,
    }) => {
      if (!cId) return;
      if (startedRef.current) return;
      startedRef.current = true;
      visitTypeRef.current = vt || null;
      bookingForRef.current = bf || null;
      setCartId(cId);
      setPackageId(pId || null);
      setOnComplete(() => (typeof cb === 'function' ? cb : null));
      await openPatients(cId, pId || null);
    },
    [openPatients]
  );

  useImperativeHandle(
    ref,
    () => ({
      start,
      reset,
    }),
    [start, reset]
  );

  const abandonFlow = useCallback(() => {
    const id = cartId;
    if (deleteCartOnAbandon && id && authToken) {
      deleteCart(id, authToken).catch((err) => console.log(err));
    }
    reset();
  }, [deleteCartOnAbandon, cartId, authToken, reset]);

  const handleCloseSlots = useCallback(() => {
    logEvent('Slot Selection Screen Dismissed', {
      cartId,
      visitType: visitTypeRef.current,
      type: 'upsell',
    });
    abandonFlow();
  }, [cartId, abandonFlow]);

  const handleSelectPatient = useCallback(
    (p) => {
      logEvent('Funnel Labs Patient Selected', {
        cartId: cartId,
        selectedPatient: p,
        type: 'upsell',
      });
      if (!cartId || !p?.patientId) return;
      setLoading(true);
      setSelectedPatient(p);
      selectPatient(cartId, p.patientId, authToken)
        .then(() => {
          setShowPatientModal(false);
          setShowAddressModal(true);
          return null;
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
    },
    [cartId, authToken]
  );

  const handleSelectAddress = useCallback(
    (a) => {
      logEvent('Funnel Labs Address Selected', {
        cartId: cartId,
        selectedAddress: a,
        type: 'upsell',
      });
      if (!cartId || !a?.addressId) return;
      setLoading(true);
      setSelectedAddress(a);
      selectAddress(a.addressId, cartId, authToken, true)
        .then(() => {
          setShowAddressModal(false);
          return openSlots(cartId);
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
    },
    [cartId, authToken, openSlots]
  );

  const currentDay = useMemo(() => {
    return safeArray(slotsDays).find((d) => d?.date === selectedDate) || null;
  }, [slotsDays, selectedDate]);

  const currentSlots = useMemo(() => {
    const day = currentDay;
    const list = day?.time || day?.slots || day?.timeSlots || [];
    return safeArray(list);
  }, [currentDay]);

  const timeOfDayTabs = useMemo(
    () => [
      {
        key: 'morning',
        label: 'Morning',
        icon: Morning,
        minHour: 0,
        maxHour: 12,
      },
      {
        key: 'afternoon',
        label: 'Afternoon',
        icon: Afternoon,
        minHour: 12,
        maxHour: 17,
      },
      {
        key: 'evening',
        label: 'Evening',
        icon: Evening,
        minHour: 17,
        maxHour: 24,
      },
    ],
    []
  );

  const activeTab = timeOfDayTabs[activeTabIndex] || timeOfDayTabs[0];
  const filteredSlots = useMemo(() => {
    const list = safeArray(currentSlots);
    if (!activeTab) return list;
    return list.filter(
      (s) =>
        typeof s?.startTime === 'number' &&
        s.startTime >= activeTab.minHour &&
        s.startTime < activeTab.maxHour
    );
  }, [currentSlots, activeTab]);

  const confirmSlotSelection = useCallback(() => {
    if (!cartId || !selectedDate || !selectedSlot) return;
    const id = selectedSlot?.slotId || selectedSlot?.id;
    if (!id) return;

    setLoading(true);
    setSelectedSlotId(id);
    selectSlot(id, cartId, selectedDate, authToken)
      .then(() => {
        setShowSlotsModal(false);
        logEvent('Slot Submitted', { cartId, visitType: visitTypeRef.current });
        const payload = {
          cartId,
          packageId,
          patient: selectedPatient,
          selectedAddress,
          selectedSlot: {
            date: selectedDate,
            time: selectedSlot.label || selectedSlot.time,
            type: 'upsell',
          },
        };
        const cb = onComplete;
        reset();
        if (cb) {
          cb(payload);
        } else {
          history.push('/ahc-upsell/order-summary', {
            cartId,
            patient: payload.patient,
            selectedSlot: payload.selectedSlot,
          });
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [
    cartId,
    selectedDate,
    selectedSlot,
    authToken,
    onComplete,
    reset,
    history,
    packageId,
    selectedPatient,
    selectedAddress,
  ]);

  const PatientModal = (
    <Modal>
      <UpsellModalOverlay onClick={(e) => e.stopPropagation()}>
        <FlyInTransition
          in={showPatientModal}
          mountOnEnter
          unmountOnExit
          appear
        >
          <UpsellModalSheetPanel>
            <div className="modalHeader">
              <div className="flex flex-row items-center">
                <img src={profileIcon} alt="" />
                <p className="ml-10px text-body-l text-txtlight-new font-semibold">
                  Select member to continue
                </p>
              </div>
              <CloseContainer onTap={abandonFlow}>
                <CloseIcon />
              </CloseContainer>
            </div>
            <div className="modalDetails">
              {safeArray(patients).map((p) => (
                <div
                  key={p.patientId}
                  className="flex flex-row justify-start items-center rounded-12 bg-txtsecondary-shades4 bg-opacity-50 mb-16px py-16px pl-16px pr-20px cursor-pointer"
                  onClick={() => handleSelectPatient(p)}
                  style={loading ? { pointerEvents: 'none', opacity: 0.7 } : {}}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      border: '1px solid #EBEAF5',
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      marginRight: '12px',
                    }}
                  >
                    {p.relation3dIcon && (
                      <img src={p.relation3dIcon} alt="logo" />
                    )}
                  </div>

                  <div className="flex flex-col">
                    <span className="text-body-l font-semibold text-txtlight">
                      {p.name}
                    </span>
                    <span className="text-body-s text-txtsecondary">
                      {p.age},{p.gender}
                    </span>
                  </div>
                </div>
              ))}
              {safeArray(patients).length === 0 && (
                <p className="text-body-s text-txtlight">No members found.</p>
              )}
            </div>
          </UpsellModalSheetPanel>
        </FlyInTransition>
      </UpsellModalOverlay>
    </Modal>
  );

  const AddressSelectionModal = showAddressModal ? (
    <AddressModal
      hideModal={() => {
        setShowAddressModal(false);
        abandonFlow();
      }}
      selectAddress={handleSelectAddress}
      selectedAddress={selectedAddress}
      serviceLine={SERVICE_LINE_NAMES.LABS}
      cartId={cartId}
      // true: do not call parent hideModal on "Next" (that was resetting the flow via abandonFlow).
      hideNativeOnSelect={true}
      modalHeaderText="Select address to continue"
      buttonText="Next"
    />
  ) : null;

  const SlotModal = (
    <Modal>
      <UpsellModalOverlay onClick={(e) => e.stopPropagation()}>
        <FlyInTransition in={showSlotsModal} mountOnEnter unmountOnExit appear>
          <UpsellModalSheetPanel>
            <div className="modalHeader" style={{ backgroundColor: '#F2F2F3' }}>
              <div className="flex flex-row items-center">
                <img src={profileIcon} alt="" />
                <p className="ml-10px text-body-l text-txtlight-new font-semibold">
                  Select slot to continue
                </p>
              </div>
              <CloseContainer onTap={handleCloseSlots}>
                <CloseIcon />
              </CloseContainer>
            </div>
            <div
              className="modalDetails"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                maxHeight: 'none',
                paddingBottom: 16,
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
              }}
            >
              <div
                className="px-20px py-16px"
                style={{ backgroundColor: '#F2F2F3' }}
              >
                <div className="flex gap-12px overflow-x-auto justify-start">
                  {safeArray(slotsDays).map((d) => {
                    const isSelected = d?.date === selectedDate;
                    const isEnabled = d?.enabled !== false;
                    return (
                      <div
                        key={d?.date}
                        className="flex flex-col items-center justify-center cursor-pointer"
                        style={{
                          minWidth: 56,
                          padding: '10px 8px',
                          borderRadius: 18,
                          border: isSelected
                            ? '1px solid #714FFF'
                            : '1px solid #EBEAF5',
                          background: isSelected ? '#714FFF' : '#ffffff',
                          opacity: isEnabled ? 1 : 0.4,
                          pointerEvents: isEnabled ? 'auto' : 'none',
                        }}
                        onClick={() => {
                          logEvent('Date Selected', {
                            day: d?.day,
                            daysFromCurrentDate: getDaysDiffFromToday(d?.date),
                            cartId,
                            visitType: visitTypeRef.current,
                            type: 'upsell',
                          });
                          setSelectedDate(d?.date || null);
                          setSelectedSlotId(null);
                          setSelectedSlot(null);
                          setActiveTabIndex(0);
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: isSelected ? 800 : 500,
                            color: isSelected ? '#ffffff' : '#767787',
                          }}
                        >
                          {d?.label || ''}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: isSelected ? '#ffffff' : '#0f0b28',
                          }}
                        >
                          {d?.day || ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!!selectedDate && (
                <div className="pb-12px px-20px mt-20px w-full">
                  <div
                    className="text-txtlight text-body-s font-semibold"
                    style={{
                      padding: '4px 0',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Select your preferred slot
                  </div>

                  <div style={{ padding: '16px 0' }}>
                    <div
                      className="flex gap-16px items-center justify-center"
                      style={{
                        alignSelf: 'center',
                        background: '#F5F4FF',
                        border: '0.5px solid #E8E8E8',
                        borderRadius: 8,
                        padding: 4,
                      }}
                    >
                      {timeOfDayTabs.map((tab, idx) => {
                        const isActive = activeTabIndex === idx;
                        return (
                          <div
                            key={tab.key}
                            className="flex items-center cursor-pointer"
                            style={{
                              background: isActive ? '#ffffff' : '#F5F4FF',
                              boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.05)',
                              borderRadius: 8,
                              padding: '8px 14px',
                            }}
                            onClick={() => {
                              setActiveTabIndex(idx);
                              setSelectedSlotId(null);
                              setSelectedSlot(null);
                            }}
                          >
                            <img
                              src={tab.icon}
                              alt={tab.label}
                              style={{ width: 20, height: 20 }}
                            />
                            <span
                              style={{
                                paddingLeft: '0.5rem',
                                fontSize: 12,
                                fontWeight: isActive ? 700 : 500,
                                color: '#413958',
                              }}
                            >
                              {tab.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <UpsellSlotGrid>
                    {filteredSlots.length > 0 ? (
                      filteredSlots.map((slot) => {
                        const id = slot?.slotId || slot?.id;
                        if (!id) return null;
                        const isSelected = selectedSlotId === id;
                        const isEnabled = slot?.enabled !== false;
                        return (
                          <UpsellSlotButton
                            key={id}
                            $selected={isSelected}
                            $enabled={isEnabled}
                            className="slot-time hover"
                            onClick={() => {
                              logEvent('Time Selected', {
                                slotTime: slot.label || slot.time,
                                cartId,
                                visitType: visitTypeRef.current,
                                type: 'upsell',
                              });
                              setSelectedSlotId(id);
                              setSelectedSlot(slot);
                            }}
                          >
                            {isSelected && (
                              <img
                                src={NewTickMark}
                                alt="tick"
                                className="mr-8px"
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-16px',
                                }}
                              />
                            )}
                            <span
                              style={{ color: '#0F0B28' }}
                              className="txt-600-12"
                            >
                              {slot.label || slot.time}
                            </span>
                          </UpsellSlotButton>
                        );
                      })
                    ) : (
                      <UpsellSlotGridEmpty
                        className="text-txtsecondary text-body-s w-full text-center"
                        style={{ padding: '16px 0' }}
                      >
                        No slots available for{' '}
                        {activeTab?.label?.toLowerCase() || 'this time'}
                      </UpsellSlotGridEmpty>
                    )}
                  </UpsellSlotGrid>
                </div>
              )}
            </div>

            <div
              className="w-full flex-shrink-0"
              style={{
                padding: '16px 20px',
                background: '#ffffff',
                borderTop: '1px solid #E2E8F0',
              }}
            >
              <button
                type="button"
                className="w-full py-16px text-body-l font-semibold"
                style={{
                  background: !selectedSlot ? '#D4D4D8' : '#714FFF',
                  color: '#ffffff',
                  opacity: loading ? 0.8 : 1,
                  pointerEvents: loading ? 'none' : 'auto',
                  borderRadius: '16px',
                }}
                disabled={!selectedSlot}
                onClick={confirmSlotSelection}
              >
                Confirm
              </button>
            </div>
          </UpsellModalSheetPanel>
        </FlyInTransition>
      </UpsellModalOverlay>
    </Modal>
  );

  if (!showPatientModal && !showAddressModal && !showSlotsModal) return null;
  if (showPatientModal) return PatientModal;
  if (showAddressModal) return AddressSelectionModal;
  return SlotModal;
});

export default UpsellBookingFlowModals;
